import React, { useMemo } from 'react';
import { Alert, Container } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import _, { every, find, isNumber, some } from 'lodash';
import TerminalMap from './TerminalMap';
import { useQuery, gql } from 'urql';
import Loading from './Loading';
import { useCurrentUser } from '../hooks';
import { HacpackQuery, HacpackQueryTerminalArguments, TerminalHandlingsArguments, Terminal, DataUpdate, TerminalArea, RiskAssessment, Segregation, Handling, DangerousGoods, HandlingType, PortAuthorityAuthorization } from '../types/graph';
import { GraphDeepPartialNotListed, GraphDeepPartialNotListedAndOverride, GraphDeepPartialOverride } from '../types';
import TerminalTeusCard from './TerminalTeusCard';
import styled from '../themes/themed-styled-components';
import RiskCard from './RiskCard';
import { GridLayout } from './ui';
import StorageTimeCard from './StorageTimeCard';
import TerminalHazardClassSummaryCard from './TerminalHazardClassSummaryCard';
import { ErrorBoundary } from "react-error-boundary";
import CenteredContainer from './CenteredContainer';
import ErrorLayout from './ErrorLayout';
import StorageDataCard from './StorageDataCard';
import { Link } from "react-router-dom";

type TerminalHomeQueryData = GraphDeepPartialOverride<HacpackQuery, {
    terminal: TerminalHomeTerminal
}>
type TerminalHomeTerminal = GraphDeepPartialNotListedAndOverride<Terminal, 'id' | 'name' | 'slug' | 'port' | 'ceiLimit' | 'centerLatitude' | 'centerLongitude' | 'centerLongitude' | 'mapZoom', {
    dataUpdate: TerminalHomeTerminalDataUpdate | null,
    areas: TerminalHomeTerminalAreaData[],
    riskAssessments: TerminalHomeTerminalRiskAssessment[],
    segregations: TerminalHomeTerminalSegregation[],
    handlings: TerminalHomeTerminalHandling[],
    mailingListsForTerminalsDataSend: undefined
}>
type TerminalHomeTerminalDataUpdate = GraphDeepPartialNotListed<DataUpdate, 'id' | 'lastUpdate'>
type TerminalHomeTerminalAreaData = GraphDeepPartialNotListed<TerminalArea, 'id' | 'terminalId' | 'name' | 'centerLatitude' | 'centerLongitude'>
type TerminalHomeTerminalRiskAssessment = GraphDeepPartialNotListed<RiskAssessment, 'id' | 'area' | 'fei' | 'cei' | 'radius'>
type TerminalHomeTerminalSegregation = Segregation
type TerminalHomeTerminalHandlingsPortAuthorityAuthorization = Pick<PortAuthorityAuthorization, 'authorized'>
type TerminalHomeTerminalHandling = GraphDeepPartialNotListedAndOverride<Handling, 'id' | 'creationDate' | 'loadingUnitIdentifier' | 'checkedIn' | 'checkedOut' | 'type' | 'loadingUnitSize' | 'loadingUnitSizeFromGate', {
    portAuthorityAuthorization: TerminalHomeTerminalHandlingsPortAuthorityAuthorization | null
    dangerousGoods: TerminalHomeTerminalHandlingsDangerousGoods[]
}>
type TerminalHomeTerminalHandlingsDangerousGoods = GraphDeepPartialNotListed<DangerousGoods, 'id' | 'adrClass'>
type TerminalHomeQueryArguments = HacpackQueryTerminalArguments & TerminalHandlingsArguments;

const terminalHomeQuery = gql<TerminalHomeQueryData, TerminalHomeQueryArguments>`
query terminal($id: Int!) {
    terminal (id: $id) {
        id
        name
        slug
        port
        ceiLimit
        feiLimit
        radiusLimit
        teusLimit
        centerLatitude
        centerLongitude
        mapZoom
        maxStorageDays
        storageWarningDays
        dataUpdate {
            id
            lastUpdate
        }
        areas {
            id
            terminalId
            name
            description
            centerLatitude
            centerLongitude
            warningThreshold
            alertThreshold
            alarmThreshold
        }
        riskAssessments {
            id
            area
            fei
            cei
            radius
            radiusBay
            radiusRow
            highRadius
        }
        segregations {
            id
            loadingUnit1Identifier
            loadingUnit2Identifier
            class1
            class2
            row1
            row2
            bay1
            bay2
            area
            terminalName
            dateOfInput
        }
        handlings {
            id
            creationDate 
            loadingUnitIdentifier
            checkedIn
            checkedOut
            type
            loadingUnitSize
            loadingUnitSizeFromGate
            storageDays
            acceptedForStorageOverTimeLimit
            portAuthorityAuthorization {
                authorized
            }
            dangerousGoods {
                id
                adrClass
            }
        }
    }
}
`;

const LeftContainer = styled(Container)`
    width: 360px;
    max-height: 100%;
    overflow-y: auto;

    > div {
        margin-bottom: 1em;   
    }
`

type TerminalHomeProps = {
    id: number
}

const TerminalHome = ({ id }: TerminalHomeProps) => {
    const [result] = useQuery<TerminalHomeQueryData, TerminalHomeQueryArguments>({
        query: terminalHomeQuery,
        variables: { id/*, types: [HandlingType.None, HandlingType.Boarding, HandlingType.Unboarding, HandlingType.TranshipmentBoarding, HandlingType.TranshipmentUnboarding]*/ }
    });


    const { data, fetching/*, error*/ } = result;

    const terminal = data?.terminal;

    const { getRolesForTerminal } = useCurrentUser();

    const { t } = useTranslation('common');

    const handlings = terminal?.handlings;

    const canSeeMap = useMemo(() => {
        if (terminal === undefined) {
            return false;
        }

        const { centerLatitude, centerLongitude, mapZoom } = terminal;

        return every([centerLatitude, centerLongitude, mapZoom], isNumber)
            || some(getRolesForTerminal(id), ['name', 'superuser']);
    }, [id, terminal, getRolesForTerminal])

    const [inYard, notFound] = useMemo(() => handlings === undefined ? [undefined, undefined]
        : _(handlings).filter('checkedIn').reduce(([iny, miss], { dangerousGoods: { length } }) => {
            //const authorized = portAuthorityAuthorization?.authorized ?? false;
            return [iny + 1, length === 0 ? miss + 1 : miss/*, authorized === true ? notAuthorized : notAuthorized + 1*/]
        }, [0, 0]),
        [handlings]);

    const loadingUnits = useMemo(() => {
        if (inYard === undefined || notFound === undefined) {
            return null;
        }

        return <p>{t('terminal.home.unitsInYard', { count: inYard as number })}{notFound > 0
            ? <>, <Link to={`/${terminal?.slug}/unita-in-sosta#statusMissingData`} className="text-danger">{t('terminal.home.unitsInYardWithNoData', { count: notFound as number })}</Link></>
            : null}</p>
    }, [inYard, notFound, t, terminal?.slug]);

    const storageData = terminal?.port !== "LA SPEZIA" ? null : notFound === undefined || notFound <= 0 ? <h4>{ t('Dati') }{' '}<span className='text-success'>{t('OK')}</span></h4> : <h4>{ t('Dati') }{' '}<span className='text-danger'>{t('KO')}</span></h4>;

    const storageDataCard = terminal?.port === "LIVORNO" ? <StorageDataCard terminal={terminal as Terminal} /> : null;

    const storageTimeCard = <StorageTimeCard terminal={terminal as Terminal} />

    const teusCard = useMemo(() => {
        if (terminal === undefined) {
            return null;
        }

        if (terminal !== undefined && (terminal.port !== 'GENOVA' || terminal.teusLimit === null)) {
            return null;
        }

        return <TerminalTeusCard teusLimit={terminal.teusLimit as number} handlings={terminal.handlings} />
    }, [terminal]);

    const terminalRiskAssessment = useMemo(() => {
        const riskAssessments = terminal?.riskAssessments;

        if (riskAssessments === undefined) {
            return undefined;
        }

        return find(riskAssessments, ['area', 'TERMINAL']) ?? null;
    }, [terminal]);

    const riskCard = <RiskCard {...terminalRiskAssessment} {...terminal} lastUpdate={terminal?.dataUpdate?.lastUpdate} />;

    const adrSummaryCard = terminal?.port === 'GENOVA' ? <TerminalHazardClassSummaryCard terminal={terminal} /> : null;

    const segregation = useMemo(() => terminal?.segregations === undefined ? null : <h4>{t('terminal.home.segregation')} {terminal.segregations.length === 0
        ? <span className="text-success">{t('data.result.ok')}</span>
        : <span className="text-danger">{t('data.result.ko')}</span>}
    </h4>, [terminal, t]);

    const cards = (inYard ?? 0) > 0 ? <>{storageData}{storageDataCard}{teusCard}{riskCard}{segregation}{storageTimeCard}{adrSummaryCard}</> : null;

    const terminalData = <LeftContainer fluid>
        <h2>{terminal?.name}</h2>

        {loadingUnits}

        {cards}
    </LeftContainer>;

    if (fetching && id !== terminal?.id) {
        return <Loading />
    }

    return (
        <GridLayout left={terminalData}>
            <ErrorBoundary fallbackRender={() => <CenteredContainer><ErrorLayout><Alert color='danger'>{t('Un errore ha impedito il caricamento della mappa.')}</Alert></ErrorLayout></CenteredContainer>}>
                {canSeeMap ? <TerminalMap key={id} terminal={terminal as TerminalHomeTerminal} /> : null}
            </ErrorBoundary>
        </GridLayout>
    );
}

export default TerminalHome;